import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { useStatisticsStore } from "../../../stores/statisticsStore";
import { BlockModalWrapper } from "../../../components/block-modal-wrapper/block-modal-wrapper";

import {
  headerContainerStyles,
  headerTextStyles,
  controlContainerStyles,
  formControlStyles,
  selectStyles,
  legendContainerStyles,
  legendItemStyles,
  legendCircleGradient,
  legendCircleSolid,
  noDataBoxStyles,
  tooltipContentStyles,
} from "./styles";

interface ChartDataItem {
  name: string;
  focus: number;
  competency: number;
}

export const CompetencyCard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const {
    competencies,
    fetchCompetencies,
    statistic,
    fetchStatistic,
    isLoadingCompetencies,
    isLoadingStatistic,
    errorCompetencies,
    errorStatistic,
    lastCompetencies,
    setLastCompetency,
  } = useStatisticsStore();

  const [selectedCompetency, setSelectedCompetency] = useState<string>("");
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectWidth, setSelectWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    fetchCompetencies();
  }, [fetchCompetencies]);

  useEffect(() => {
    if (lastCompetencies) {
      setSelectedCompetency(lastCompetencies.competency);
    }
  }, [lastCompetencies]);

  useEffect(() => {
    if (selectedCompetency) {
      fetchStatistic(selectedCompetency);
    }
  }, [selectedCompetency, fetchStatistic]);

  useEffect(() => {
    if (!statistic || statistic.length === 0) {
      setChartData([]);
      return;
    }

    try {
      if (
        statistic[0] &&
        "competency" in statistic[0] &&
        "focus" in statistic[0]
      ) {
        setChartData(statistic);
      } else {
        const transformedData = statistic.map((item: any) => ({
          name: item.name || "",
          focus: item["Focus Area Performance"] || 0,
          competency: item["Selected Competency Performance"] || 0,
        }));
        setChartData(transformedData);
      }
    } catch (error) {
      console.error("Error transforming statistic data:", error);
      setChartData([]);
    }
  }, [statistic]);

  useEffect(() => {
    const updateSelectWidth = () => {
      if (selectRef.current) {
        const width = selectRef.current.offsetWidth;
        setSelectWidth(width);
      }
    };

    const timeoutId = setTimeout(updateSelectWidth, 0);
    window.addEventListener("resize", updateSelectWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateSelectWidth);
    };
  }, [isMobile, isTablet, selectedCompetency]);

  const handleCompetencyChange = (
    event: SelectChangeEvent<string>,
    child?: React.ReactNode
  ) => {
    const value = event.target.value;
    setSelectedCompetency(value);
    const selected = competencies.find((comp) => comp.competency === value);
    if (selected) {
      setLastCompetency(selected);
    }
  };

  const chartConfig = useMemo(() => {
    const dataLength = chartData.length || 0;

    const getBarSize = () => {
      if (dataLength === 0) return 30;

      if (isMobile) {
        if (dataLength > 10) return 10;
        if (dataLength > 6) return 15;
        return 20;
      }

      if (isTablet) {
        if (dataLength > 12) return 15;
        if (dataLength > 8) return 20;
        return 25;
      }

      if (dataLength > 15) return 15;
      if (dataLength > 10) return 20;
      if (dataLength > 6) return 25;
      return 30;
    };

    const getXAxisInterval = () => {
      if (isMobile) {
        if (dataLength > 6) return Math.floor(dataLength / 4);
        return 0;
      }

      if (isTablet) {
        if (dataLength > 10) return Math.floor(dataLength / 5);
        return 0;
      }

      if (dataLength > 12) return Math.floor(dataLength / 6);
      return 0;
    };

    const getXAxisFontSize = () => {
      if (isMobile) {
        if (dataLength > 10) return 8;
        return 10;
      }

      if (isTablet) {
        if (dataLength > 15) return 8;
        if (dataLength > 10) return 10;
        return 11;
      }

      if (dataLength > 20) return 9;
      if (dataLength > 15) return 10;
      return 12;
    };

    const getChartMargin = () => {
      if (isMobile) {
        return { top: 15, right: 5, left: -20, bottom: 30 };
      }

      if (isTablet) {
        return { top: 20, right: 10, left: -15, bottom: 30 };
      }

      return { top: 20, right: 20, left: -15, bottom: 30 };
    };
    const getXAxisTickAngle = () => {
      return -45;
    };
    const shouldTickRotate = true;

    const getChartHeight = () => {
      if (isMobile) return 220;
      if (isTablet) return 250;
      return 285;
    };

    return {
      barSize: getBarSize(),
      xAxisInterval: getXAxisInterval(),
      xAxisFontSize: getXAxisFontSize(),
      margin: getChartMargin(),
      xAxisTickAngle: getXAxisTickAngle(),
      shouldTickRotate,
      chartHeight: getChartHeight(),
      lineStrokeWidth: isMobile ? 2 : dataLength > 15 ? 2 : 3,
      dotRadius: isMobile ? 2 : dataLength > 15 ? 2 : 3,
      activeDotRadius: isMobile ? 3 : dataLength > 15 ? 3 : 4,
    };
  }, [chartData.length, isMobile, isTablet]);

  const CustomizedAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const angle = chartConfig.xAxisTickAngle;

    const getTruncatedLabel = (label: string) => {
      if (!label) return "";

      const maxLength = 6;
      return label.length > maxLength
        ? `${label.substring(0, maxLength)}...`
        : label;
    };

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={10}
          textAnchor="end"
          fill={theme.palette.modalColors.statisticColor || "#666"}
          fontSize={10}
          transform={`rotate(${angle})`}
        >
          {getTruncatedLabel(payload.value)}
        </text>
      </g>
    );
  };

  const responsiveStyles = {
    chartContainer: {
      height: chartConfig.chartHeight,
      width: "100%",
      position: "relative" as const,
      px: isMobile ? 0 : 1,
      boxSizing: "border-box" as const,
      maxWidth: "100%",
      overflow: "hidden",
    },
    legendContainer: {
      ...legendContainerStyles,
      flexDirection: isMobile ? ("column" as const) : ("row" as const),
      alignItems: isMobile ? ("flex-start" as const) : ("center" as const),
      mt: isMobile ? 1 : 0,
    },
    formControl: {
      ...formControlStyles,
      width: { xs: "100%", sm: "100%", md: "100%" },
      minWidth: { md: "400px" },
      maxWidth: { md: "500px" },
    },
    tooltipContentStyle: {
      ...tooltipContentStyles,
      padding: "8px",
      fontSize: isMobile ? "11px" : "12px",
      maxWidth: isMobile ? "200px" : "300px",
      whiteSpace: "normal",
      wordBreak: "keep-all",
    },
    tooltipWrapperStyle: {
      zIndex: 100,
      pointerEvents: "none" as const,
      width: "auto",
      maxWidth: "100%",
    },
  };

  return (
    <BlockModalWrapper lable={""}>
      <Typography sx={headerTextStyles}>Competency</Typography>
      <Box sx={headerContainerStyles}>
        <Box sx={controlContainerStyles}>
          <Box ref={selectRef} sx={{ width: "100%" }}>
            <FormControl
              variant="outlined"
              sx={responsiveStyles.formControl}
              fullWidth
            >
              <InputLabel sx={{ color: "#06B6D4" }} id="competency-label">
                Competency
              </InputLabel>
              <Select
                labelId="competency-label"
                label="Competency"
                value={selectedCompetency}
                onChange={handleCompetencyChange}
                sx={{
                  ...selectStyles,
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      width: selectWidth ? `${selectWidth}px` : "auto",
                      "& .MuiMenuItem-root": {
                        display: "block",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflowWrap: "anywhere",
                        padding: isMobile ? "6px 10px" : "8px 16px",
                        fontSize: isMobile ? "13px" : "inherit",
                      },
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {isLoadingCompetencies ? (
                  <MenuItem
                    disabled
                    color={theme.palette.modalColors.colorLable}
                  >
                    Loading...
                  </MenuItem>
                ) : errorCompetencies ? (
                  <MenuItem disabled>{errorCompetencies}</MenuItem>
                ) : (
                  competencies.map((comp, index) => (
                    <MenuItem key={index} value={comp.competency}>
                      {comp.competency} {comp?.score}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
          <Box sx={responsiveStyles.legendContainer}>
            <Box sx={legendItemStyles}>
              <Box sx={legendCircleGradient} />
              <CustomTypography
                fontSize={isMobile ? "12px" : "14px"}
                color="#828282"
              >
                Selected Competency Performance
              </CustomTypography>
            </Box>
            <Box sx={legendItemStyles}>
              <Box sx={legendCircleSolid} />
              <CustomTypography
                fontSize={isMobile ? "12px" : "14px"}
                color="#828282"
              >
                Focus Area Performance
              </CustomTypography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={responsiveStyles.chartContainer}>
        {isLoadingStatistic ? (
          <CustomTypography>Loading statistic...</CustomTypography>
        ) : errorStatistic ? (
          <CustomTypography color="red">{errorStatistic}</CustomTypography>
        ) : chartData.length === 0 ? (
          <Box sx={noDataBoxStyles}>
            <CustomTypography color={theme.palette.modalColors.colorLable}>
              Please, choose the competency
            </CustomTypography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={chartConfig.margin}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={isMobile ? "rgba(0,0,0,0.05)" : "none"}
                horizontal={!isMobile}
              />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={15}
                color={theme.palette.modalColors.statisticColor}
                interval={0}
                tick={CustomizedAxisTick}
                height={40}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                orientation="left"
                tickMargin={isMobile ? -15 : -20}
                color={theme.palette.modalColors.statisticColor}
                width={40}
                tick={{
                  fontSize: isMobile ? 10 : 12,
                }}
                tickCount={isMobile ? 4 : 5}
              />
              <Tooltip
                cursor={false}
                content={(props) => {
                  if (
                    !props.active ||
                    !props.payload ||
                    props.payload.length === 0
                  ) {
                    return null;
                  }
                  return (
                    <div
                      style={{
                        padding: "4px 6px",
                        fontSize: "10px",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        border: "1px solid #E0E0E0",
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                        width: "auto",
                        maxWidth: "140px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "2px" }}>
                        {props.payload[0]?.payload?.name || ""}
                      </div>
                      <div style={{ fontWeight: "bold" }}>
                        {props.payload[0]?.name || ""}:{" "}
                        {props.payload[0]?.value ?? ""}
                      </div>
                      <div style={{ fontWeight: "bold", color: "#66EF64" }}>
                        {props.payload[1]?.name || ""}:{" "}
                        {props.payload[1]?.value || ""}
                      </div>
                    </div>
                  );
                }}
                wrapperStyle={{
                  zIndex: 100,
                  pointerEvents: "none",
                  outline: "none",
                }}
              />
              <defs>
                <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#28C9DC" />
                  <stop offset="100%" stopColor="#8957CF" />
                </linearGradient>
              </defs>
              <Bar
                dataKey="competency"
                fill="url(#barColor)"
                barSize={chartConfig.barSize}
                radius={[8, 8, 0, 0]}
                name="Selected Competency Performance"
              />
              <Line
                type="monotone"
                dataKey="focus"
                stroke="#66EF64"
                strokeWidth={chartConfig.lineStrokeWidth}
                dot={{ r: chartConfig.dotRadius }}
                activeDot={{ r: chartConfig.activeDotRadius }}
                name="Focus Area Performance"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>
    </BlockModalWrapper>
  );
};

export default CompetencyCard;
